import { useEffect, useState } from "react";
import { NavLink, Route, Routes } from "react-router";
import { useAuth } from "./auth/AuthProvider";
import { SignIn } from "./auth/SignIn";
import { authedFetch } from "./lib/api";
import { Planner } from "./pages/Planner";
import { Showtimes } from "./pages/Showtimes";
import { Tracker } from "./pages/Tracker";

// Phase 3 of the Streamlit -> React migration: the first page that does real
// work. Everything except the signed-in view is still the auth shell from
// Phase 2, which exists to keep the four states (idle / verifying / allowed /
// unavailable / denied) distinguishable -- see AuthProvider for why
// "unavailable" is not "denied".
//
// The Movie Finder stays at `/`. That is a constraint, not a preference: the
// amc-seat-extension's sync.js gates its resolved-link write-back on
// `pathname !== '/'`, so moving this page to a sub-route would silently break
// extension sync with no error visible in either codebase.

function ApiVersion() {
  const [version, setVersion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    authedFetch("/api/version")
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((body: { version: string }) => {
        if (active) setVersion(body.version);
      })
      .catch((err: Error) => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, []);

  // Which image is actually serving. Worth surfacing rather than logging: the
  // API and the Streamlit app deploy from ONE build, so a mismatch against the
  // short SHA in the Streamlit footer means a half-completed rollout.
  return (
    <span className="font-mono">
      {error ? `unreachable (${error})` : (version ?? "…")}
    </span>
  );
}

/** The centered card the pre-signed-in states share. */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-8">
      <div className="w-full max-w-md space-y-6">
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">AMC Movies</h1>
          <p className="text-sm text-neutral-400">
            Showtimes, ratings, and planning.
          </p>
        </header>
        {children}
      </div>
    </div>
  );
}

/** The three pages that are part of the weekly loop.
 *
 *  A real nav bar, not a footer link. The three are genuinely peers -- find a
 *  showtime, plan it, log it once you have been -- and until now the planner
 *  was reachable only from small grey text at the bottom of the page, which
 *  read as "diagnostic" rather than "the second thing you do".
 *
 *  The sync queue is deliberately NOT here; see the footer.
 */
const NAV = [
  { to: "/", label: "🎬 Finder" },
  { to: "/movie_planner", label: "📅 Planner" },
  { to: "/movie_tracker", label: "🎟️ Tracker" },
];

function PrimaryNav() {
  return (
    <nav className="border-b border-neutral-900">
      <div className="mx-auto flex max-w-3xl gap-1 px-4">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            // `end` on the Finder only, to say "active when the path IS this".
            // NOT load-bearing today: react-router 7's NavLink already declines
            // to mark `to="/"` active on `/movie_tracker`, so removing this
            // changes nothing and no test can see it (checked directly, not
            // assumed -- the v6 "root link matches every route" gotcha is what
            // this looks like it is guarding, and that gotcha is gone). It
            // stays because it is free and states the intent, and it starts
            // mattering again the day anything nests under `/`.
            end={item.to === "/"}
            className={({ isActive }) =>
              "px-3 py-2.5 text-sm " +
              (isActive
                ? "border-b-2 border-neutral-300 font-medium text-neutral-100"
                : "text-neutral-500 hover:text-neutral-300")
            }
          >
            {item.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export default function App() {
  const { state, signOut, retry } = useAuth();

  if (state.status === "allowed") {
    return (
      <div className="min-h-screen">
        <PrimaryNav />
        {/* The Movie Finder MUST stay at `/` and the planner at
            `/movie_planner`. The amc-seat-extension gates its two write-backs
            on exactly those pathnames (`resolved_amc_links` on `/`,
            `captured_seats` on a path ending in `/movie_planner`), so a
            rename here silently breaks extension sync with no error visible in
            either codebase. Nothing gates on `/movie_tracker`, but it matches
            the Streamlit path so old bookmarks keep working. */}
        <Routes>
          <Route path="/" element={<Showtimes />} />
          <Route path="/movie_planner" element={<Planner />} />
          <Route path="/movie_tracker" element={<Tracker />} />
          {/* Anything else is a typo or a stale bookmark; the Finder is the
              app's front door, and a 404 page would be more chrome than the
              case deserves. */}
          <Route path="*" element={<Showtimes />} />
        </Routes>
        <footer className="mx-auto max-w-3xl border-t border-neutral-900 px-4 py-4 text-xs text-neutral-600">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>
              Signed in as <span className="font-mono">{state.user.email ?? "—"}</span>
            </span>
            {/* No Letterboxd sync link. That page showed a queue of pending
                watchlist writes; the star writes throw-rag inline now, so
                there is nothing to queue, nothing to drain and nothing to
                un-stick. A failed write is reported on the click that made
                it. */}
            <button onClick={signOut} className="underline hover:text-neutral-300">
              Sign out
            </button>
            <span className="ml-auto">
              build <ApiVersion />
            </span>
          </div>
          {/* No "the rest is still at movies.mattfinkel.com" any more: every
              page the Streamlit app had exists here. That old line outlived
              its truth by one commit, which is the usual fate of copy
              describing a migration. */}
          <p className="mt-2 text-neutral-700">
            Showtimes from Fandango and AMC; ratings from TMDb, OMDb and Rotten
            Tomatoes.
          </p>
        </footer>
      </div>
    );
  }

  return (
    <Shell>
      {state.status === "idle" && <SignIn />}

      {state.status === "verifying" && (
        <div className="rounded-md border border-neutral-800 bg-neutral-900 p-4 text-sm">
          Verifying access…
        </div>
      )}

      {/* Amber, not red, and it offers Try again rather than Sign in: this is
          "we could not ask", so telling the user to re-authenticate would send
          them to fix something that is not broken on their end. */}
      {state.status === "unavailable" && (
        <div className="space-y-3">
          <div
            role="status"
            className="rounded-md border border-amber-900 bg-amber-950/40 p-4 text-sm"
          >
            Could not verify access: {state.reason}. You are still signed in as{" "}
            <span className="font-mono">{state.user.email ?? "—"}</span>.
          </div>
          <button
            onClick={retry}
            className="rounded-md bg-neutral-100 px-4 py-2 text-neutral-900 hover:bg-white"
          >
            Try again
          </button>
          <button
            onClick={signOut}
            className="block text-sm text-neutral-400 underline hover:text-neutral-200"
          >
            Sign out
          </button>
        </div>
      )}

      {state.status === "denied" && (
        <div className="space-y-3">
          <div role="alert" className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">
            Access denied: {state.reason}
          </div>
          <SignIn />
        </div>
      )}
    </Shell>
  );
}
